import { useEffect, useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { DatePicker } from "./DatePicker";
import {
  type DatePickerDataProps,
  datePickerDataSchema,
} from "./DatePicker.schema";
import { DatePickerContent } from "./DatePickerContent";
import { DatePickerTrigger } from "./DatePickerTrigger";
import { DatePickerValue } from "./DatePickerValue";
import { DATE_PICKER_TAG_NAME } from "./constants";

export interface DatePickerHandle {
  value: (value: string | null) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [DATE_PICKER_TAG_NAME]: {
      props: DatePickerDataProps;
      handle: DatePickerHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-date-picker": HTMLElement & DatePickerHandle;
  }
}

export function RWCDatePicker({
  ref,
  value: initialValue,
  disabled: initialDisabled,
  placeholder,
  ariaLabel,
  locale,
  min,
  max,
  arrow,
  size,
  layout,
}: DatePickerDataProps & { ref?: Ref<DatePickerHandle> }) {
  const host = useIslandHost();

  const [value, setValue] = useState<string | null>(initialValue ?? null);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);

  useImperativeHandle(
    ref,
    () => ({
      value: setValue,
      disabled: (nextDisabled = true) => setDisabled(nextDisabled),
    }),
    [],
  );

  useEffect(() => {
    getElementInternals(host)?.setFormValue(disabled ? null : value);
  }, [host, value, disabled]);

  function handleValueChange(next: string | null) {
    setValue(next);
    host?.dispatchEvent(
      new CustomEvent("date-picker:change", {
        detail: { value: next },
        bubbles: true,
      }),
    );
  }

  return (
    <DatePicker
      locale={locale?.replace("_", "-")}
      value={value}
      onValueChange={handleValueChange}
      disabled={disabled}
      min={min}
      max={max}
      arrow={arrow}
      size={size}
      layout={layout}
    >
      <DatePickerTrigger aria-label={ariaLabel}>
        <DatePickerValue placeholder={placeholder} />
      </DatePickerTrigger>
      <DatePickerContent />
    </DatePicker>
  );
}

componentManager.register(
  rwc(DATE_PICKER_TAG_NAME, {
    component: RWCDatePicker,
    dataSchema: datePickerDataSchema,
    formAssociated: true,
  }),
);
